import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FileText } from 'lucide-react';

const STATUS_TONES = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-400/25',
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25',
  blue: 'bg-blue-500/15 text-blue-300 border-blue-400/25',
  red: 'bg-red-500/15 text-red-300 border-red-400/25',
  slate: 'bg-white/10 text-white/70 border-white/15',
};

function DocumentStatusBadge({ label, tone = 'slate' }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
        STATUS_TONES[tone] || STATUS_TONES.slate
      }`}
    >
      {label}
    </span>
  );
}

export default function AIAgentRecentDocuments({
  documents = [],
  isLoading = false,
  onTreatDocument,
  showWhenEmpty = false,
}) {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.aiAgent.recentDocuments.${key}`, opts);

  if (isLoading) {
    return (
      <section className="mx-auto w-full max-w-4xl mb-4">
        <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden animate-pulse">
          <div className="px-5 sm:px-6 py-4 border-b border-white/10">
            <div className="h-5 w-40 bg-white/10 rounded-lg" />
          </div>
          <div className="px-5 sm:px-6 py-8 space-y-3">
            <div className="h-10 bg-white/5 rounded-2xl" />
            <div className="h-10 bg-white/5 rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  if (!documents.length && !showWhenEmpty) return null;

  return (
    <section className="mx-auto w-full max-w-4xl mb-4">
      <div className="rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] via-blue-950/20 to-indigo-950/30 backdrop-blur-lg overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <FileText className="h-5 w-5 text-white/70 shrink-0" />
          <h2 className="m-0 text-base sm:text-lg font-semibold text-white">{p('title')}</h2>
        </div>

        {documents.length === 0 ? (
          <p className="m-0 px-5 sm:px-6 py-8 text-sm text-white/45 text-center">{p('empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse">
              <thead>
                <tr className="text-left text-xs sm:text-sm text-white/45">
                  <th className="px-5 sm:px-6 py-3 font-medium">{p('col_document')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_client')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_amount')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_status')}</th>
                  <th className="px-3 py-3 font-medium">{p('col_date')}</th>
                  <th className="px-3 py-3 w-28" aria-label={p('col_actions_aria')} />
                </tr>
              </thead>
              <tbody>
                {documents.map((doc, index) => (
                  <tr
                    key={doc.id}
                    className={`group transition-colors ${
                      index < documents.length - 1 ? 'border-b border-white/[0.06]' : ''
                    } hover:bg-white/[0.03]`}
                  >
                    <td className="px-5 sm:px-6 py-4">
                      <button
                        type="button"
                        onClick={() => onTreatDocument?.(doc)}
                        className="text-left"
                      >
                        <p className="m-0 text-sm font-semibold text-white hover:text-blue-200 transition-colors">
                          {doc.number}
                        </p>
                        <p className="m-0 mt-0.5 text-xs text-white/45">{doc.typeLabel}</p>
                      </button>
                    </td>
                    <td className="px-3 py-4 text-sm text-white/70 max-w-[160px] truncate">
                      {doc.client}
                    </td>
                    <td className="px-3 py-4 text-sm text-white/80 whitespace-nowrap">
                      {doc.amount}
                    </td>
                    <td className="px-3 py-4">
                      <DocumentStatusBadge label={doc.statusLabel} tone={doc.statusTone} />
                    </td>
                    <td className="px-3 py-4 text-sm text-white/50 whitespace-nowrap">
                      {doc.dateLabel}
                    </td>
                    <td className="px-3 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => onTreatDocument?.(doc)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold border border-white/20 text-white hover:bg-white/10 transition-colors"
                      >
                        {p('treat')}
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
